import { h, defineComponent, ref, watch } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, loader, pagination } from '@/ui/components';
import { GATEWAY_STATUS, list as getGatewayList } from '@/services/api/resources/gateway';
import { emptyList } from '@/services/api';
import { usePagination, useTask } from '@/ui/concerns';
import { ResourceTable } from '@/ui/components/table/ResourceTable';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { column } from '@/ui/components/table/TableColumn';
import { GatewayList, GatewayRecord } from '@/entities/gateway';
import { renderForbiddenPage } from '@/ui/pages/error/ForbiddenPage';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';

export const GatewayIndexPage = defineComponent({
  name: 'GatewayIndexPage',
  setup() {
    if (!hasFeature('sensors')) {
      return renderResourceTeaserPage(t('common.sensors'));
    }

    if (!hasPermission(Permissions.MANAGE_HARDWARE)) {
      return renderForbiddenPage;
    }

    // initial loading
    const initialLoading = ref(true);

    // state stored in query string
    const { page, perPage } = usePagination();

    // get list task
    const list = ref<GatewayList>(emptyList());
    const getList = useTask(
      () => getGatewayList({
        page: page.value,
        perPage: perPage.value,
      }),
      listResponse => {
        list.value = listResponse;
        initialLoading.value = false;
      },
    );
    watch([page, perPage], getList.run);

    return function render() {
      return h('div', [
        box([
          loader('div', getList, [
            h(ResourceTable, {
              props: {
                editRoute: 'gatewayEdit',
                list: list.value.data,
              },
              scopedSlots: {
                title: ({ row: gateway }: { row: GatewayRecord }) => gateway.data.title,
                default: () => [
                  column({
                    field: 'entityItem',
                    label: t('common.entity-item'),
                  }, {
                    default: ({ row: gateway }: { row: GatewayRecord }) =>
                      gateway.data.entityItem ? gateway.data.entityItem.data.title : null,
                  }),
                  column({
                    field: 'deviceId',
                    label: t('gateways.deviceId'),
                  }, {
                    default: ({ row: gateway }: { row: GatewayRecord }) => gateway.data.deviceId,
                  }),
                  column({
                    field: 'status',
                    label: t('common.status'),
                  }, {
                    default: ({ row: gateway }: { row: GatewayRecord }) =>
                      gateway.data.status.connected === GATEWAY_STATUS.CONNECTED
                        ? t('gateways.connected') : t('gateways.not-connected'),
                  }),
                ],
              },
            }),
            pagination({
              current: page.value,
              total: list.value.meta.total,
              perPage: perPage.value,
            }),
          ], !initialLoading.value),
        ]),
      ]);
    };
  },
});
